import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faCirclePlus, faInfoCircle } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, Text } from "@performyard/py-components";

import type { FormQuestionSuggestionStatus, FormQuestionSuggestionWithId } from "../types";

import { QUESTION_TYPE_LABEL } from "../utils/question-type-labels";

type FormQuestionPreviewCardProps = {
	suggestion: FormQuestionSuggestionWithId;
	status: FormQuestionSuggestionStatus;
	onAdd: () => void;
	onDelete: () => void;
};

export const FormQuestionPreviewCard: React.FC<FormQuestionPreviewCardProps> = ({
	suggestion,
	status,
	onAdd,
	onDelete,
}) => {
	if (status === "deleted") return null;

	const isAdded = status === "added";

	const stringChoices =
		(suggestion.type === "multiple-choice" || suggestion.type === "multiple-select") && suggestion.choices
			? suggestion.choices
			: undefined;

	return (
		<div className="flex max-w-full min-w-0 flex-col gap-0">
			<div className="box-border flex max-w-full min-w-0 flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-1 p-3">
				<Text size="extra-small" weight="semi-bold" color="neutral-7">
					{QUESTION_TYPE_LABEL[suggestion.type]}
				</Text>
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					{suggestion.question}
					{suggestion.is_required ? <span className="text-red-7"> *</span> : null}
				</Text>
				{suggestion.subtext ? (
					<Text size="caption" color="neutral-7">
						{suggestion.subtext}
					</Text>
				) : null}

				{stringChoices && stringChoices.length > 0 ? (
					<ul className="m-0 flex list-none flex-col gap-1 p-0">
						{stringChoices.map((c, idx) => (
							<li key={`${suggestion.id}-choice-${idx}`} className="flex items-center gap-2">
								<span
									aria-hidden
									className="inline-block h-3 w-3 shrink-0 rounded-full border border-solid border-neutral-4 bg-transparent"
								/>
								<Text size="caption" color="neutral-9">
									{c}
								</Text>
							</li>
						))}
					</ul>
				) : null}

				{suggestion.type === "rating-scale" && suggestion.rating_scale && suggestion.rating_scale.length > 0 ? (
					<ul className="m-0 flex list-none flex-col gap-1 p-0">
						{suggestion.rating_scale.map((opt, idx) => (
							<li key={`${suggestion.id}-rs-${idx}`} className="flex items-center gap-2">
								<FontAwesomeIcon
									icon={faInfoCircle}
									className="h-3 w-3 shrink-0 text-neutral-4"
									aria-hidden
								/>
								<Text size="caption" color="neutral-9">
									{opt.name}
								</Text>
							</li>
						))}
					</ul>
				) : null}
			</div>

			<div className="flex max-w-full min-w-0 items-center justify-between">
				<div className="shrink-0">
					{!isAdded && (
						<ButtonIcon
							icon={faTrashCan}
							onClick={onDelete}
							variant="standard"
							color="neutral"
							shade={5}
							size={16}
							aria-label="Delete question suggestion"
						/>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-1">
					{isAdded ? (
						<span className="inline-flex items-center gap-1 px-2 py-1 text-sm text-green-5">
							<FontAwesomeIcon icon={faCircleCheck} className="text-green-5" aria-hidden />
							<Text size="caption" weight="semi-bold" color="green-5">
								Question Added
							</Text>
						</span>
					) : (
						<ButtonStandard
							variant="text"
							color="primary"
							size="medium"
							iconLeft={faCirclePlus}
							onClick={onAdd}
							dangerouslySetClassName="!w-auto"
						>
							Add Question
						</ButtonStandard>
					)}
				</div>
			</div>
		</div>
	);
};
